import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Sparkles, ShieldCheck, Flag, Mail, Phone, Share2, ArrowLeft } from "lucide-react";

const BADGE = {
  official: { bg: "var(--wofy-secondary)", label: "OFFICIAL SOCIAL PULSE POST", icon: Sparkles },
  verified_org: { bg: "var(--wofy-primary)", label: "VERIFIED ORGANIZATION", icon: ShieldCheck },
  verified_authority: { bg: "#5C6B52", label: "VERIFIED BY AUTHORITY", icon: ShieldCheck },
  self_reported: { bg: "#7A7A7A", label: "SELF-REPORTED", icon: null },
};

const URGENCY = {
  emergency: { bg: "#B23A3A", label: "EMERGENCY" },
  high: { bg: "#B25D38", label: "HIGH" },
  normal: { bg: "var(--wofy-primary)", label: "NORMAL" },
  low: { bg: "#7A7A7A", label: "LOW" },
};

export default function CauseDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(null);
  const [activePhoto, setActivePhoto] = useState(0);

  useEffect(() => {
    http.get(`/causes/${id}`).then((r) => setC(r.data)).catch((e) => setErr(e?.response?.status || 500));
  }, [id]);

  const share = async () => {
    try {
      await navigator.share({ title: c.title, url: window.location.href });
    } catch {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied");
    }
  };

  if (err) return <div className="max-w-[820px] mx-auto px-6 py-24 text-center"><h1 className="font-display text-3xl">Cause not found.</h1><Link to="/causes" className="wofy-btn-primary mt-6">Browse causes</Link></div>;
  if (!c) return <div className="max-w-[820px] mx-auto px-6 py-24 text-center">Loading…</div>;

  const badge = BADGE[c.verification_badge] || BADGE.self_reported;
  const Bi = badge.icon;
  const urg = URGENCY[c.urgency] || URGENCY.normal;

  return (
    <article data-testid="cause-detail" className="max-w-[900px] mx-auto px-6 lg:px-10 py-16">
      <Link to="/causes" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider mb-6 hover:opacity-80" style={{ color: "var(--wofy-text-soft)" }}>
        <ArrowLeft className="w-3 h-3" /> Back to causes
      </Link>

      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider text-white" style={{ background: badge.bg }}>
          {Bi && <Bi className="w-3 h-3" />} {badge.label}
        </span>
        <span className="inline-block px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider text-white" style={{ background: urg.bg }}>{urg.label}</span>
        <span className="wofy-tag text-[10px]">{c.category}{c.subcategory ? ` · ${c.subcategory}` : ""}</span>
      </div>

      <h1 className="mt-4 font-display text-4xl md:text-5xl leading-[1.05]">{c.title}</h1>
      <div className="mt-3 text-sm flex flex-wrap items-center gap-4" style={{ color: "var(--wofy-text-soft)" }}>
        <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {[c.location.city, c.location.state, c.location.country].filter(Boolean).join(", ")}</span>
        <span>Posted by {c.author_display || "Helper"}</span>
        {c.published_at && <span>{new Date(c.published_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</span>}
      </div>

      {c.photos?.length > 0 && (
        <div className="mt-8">
          <div className="aspect-[16/9] rounded-2xl overflow-hidden">
            <img src={c.photos[activePhoto]} alt="" className="w-full h-full object-cover" />
          </div>
          {c.photos.length > 1 && (
            <div className="mt-3 flex gap-2 flex-wrap">
              {c.photos.map((p, i) => (
                <button key={i} onClick={() => setActivePhoto(i)} className={`w-16 h-16 rounded-lg overflow-hidden ${i === activePhoto ? "ring-2" : ""}`} style={{ ringColor: "var(--wofy-primary)" }}>
                  <img src={p} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-8 wofy-card whitespace-pre-line text-base leading-relaxed">
        {c.description}
      </div>

      {c.video_links?.length > 0 && (
        <div className="mt-6">
          <div className="overline">VIDEOS</div>
          <ul className="mt-2 space-y-1">
            {c.video_links.map((v, i) => (
              <li key={i}><a href={v} target="_blank" rel="noopener noreferrer" className="text-sm underline" style={{ color: "var(--wofy-primary)" }}>{v}</a></li>
            ))}
          </ul>
        </div>
      )}

      {c.help_types?.length > 0 && (
        <div className="mt-8">
          <div className="overline mb-2">TYPES OF HELP NEEDED</div>
          <div className="flex flex-wrap gap-2">
            {c.help_types.map((h) => <span key={h} className="wofy-tag">{h}</span>)}
          </div>
        </div>
      )}

      {(c.social_links && Object.values(c.social_links).some(Boolean)) && (
        <div className="mt-8">
          <div className="overline mb-2">LINKS</div>
          <ul className="text-sm space-y-1">
            {Object.entries(c.social_links).filter(([, v]) => v).map(([k, v]) => (
              <li key={k}><a href={v} target="_blank" rel="noopener noreferrer" className="underline capitalize">{k}: {v}</a></li>
            ))}
          </ul>
        </div>
      )}

      {(c.contact_email || c.contact_phone || c.verified_contact_only) && (
        <div className="mt-8 wofy-card">
          <div className="overline">HOW TO REACH THIS PERSON</div>
          {c.verified_contact_only ? (
            <p className="mt-2 text-sm">This person has requested that only <b>verified volunteers</b> contact them. Log in with a verified account to see contact info.</p>
          ) : (
            <div className="mt-3 space-y-2 text-sm">
              {c.contact_email && <div className="flex items-center gap-2"><Mail className="w-4 h-4" /> <a href={`mailto:${c.contact_email}`} className="underline">{c.contact_email}</a></div>}
              {c.contact_phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4" /> {c.contact_phone}</div>}
            </div>
          )}
        </div>
      )}

      <div className="mt-10 flex flex-wrap gap-3">
        <button onClick={share} className="wofy-btn-ghost text-sm"><Share2 className="w-4 h-4" /> Share</button>
        <Link to="/donate" className="wofy-btn-primary text-sm">Donate</Link>
        <button onClick={() => toast.info("Reported. Our moderators will review.")} data-testid="cause-report" className="wofy-btn-ghost text-sm" style={{ color: "#B23A3A" }}>
          <Flag className="w-4 h-4" /> Report abuse
        </button>
      </div>
    </article>
  );
}
